import { auth, getRealTimeDocument, logoutUser } from '../config/data/firebase';
import { onAuthStateChanged, type User } from 'firebase/auth';
import { createContext, useState, useEffect, useMemo, useCallback, useRef, useContext, type ReactNode } from 'react';
import Loader from '../components/loader/Loader';
import { UserType, collections } from '../config/data/collections';
import type { Applicant, Member } from '../types';
import type { UserTypeValue } from '../config/data/collections';
import { usePresenceHeartbeat } from '../hooks/usePresenceHeartbeat';
import { hasMobileAuthInUrl, installMobileWebViewAuth } from '../utils/mobileWebViewAuth';

function hasMobileAuthPendingOnBoot(): boolean {
	return hasMobileAuthInUrl();
}

export interface AuthContextValue {
	user: User | null;
	member: Member | null;
	applicant: Applicant | null;
	role: UserTypeValue | null;
	loading: boolean;
	logout: () => void;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export const AuthProvider = ({ children }: { children: ReactNode }) => {
	const [user, setUser] = useState<User | null>(null);
	const [member, setMember] = useState<Member | null>(null);
	const [applicant, setApplicant] = useState<Applicant | null>(null);
	const [loading, setLoading] = useState(true);
	/** Expo in-app browser handoff: stay on Loader until custom-token sign-in settles. */
	const [mobileHandoffPending, setMobileHandoffPending] = useState(() =>
		typeof window !== 'undefined' ? hasMobileAuthPendingOnBoot() : false,
	);

	const applicantUnsubscribeRef = useRef<(() => void) | null>(null);
	const memberUnsubscribeRef = useRef<(() => void) | null>(null);
	const userUidRef = useRef<string | null>(null);
	const mobileHandoffPendingRef = useRef(mobileHandoffPending);

	const stopProfileListeners = useCallback(() => {
		if (applicantUnsubscribeRef.current) {
			applicantUnsubscribeRef.current();
			applicantUnsubscribeRef.current = null;
		}
		if (memberUnsubscribeRef.current) {
			memberUnsubscribeRef.current();
			memberUnsubscribeRef.current = null;
		}
	}, []);

	const clearUserData = useCallback(() => {
		stopProfileListeners();
		userUidRef.current = null;
		setUser(null);
		setMember(null);
		setApplicant(null);
	}, [stopProfileListeners]);

	const subscribeToProfiles = useCallback((uid: string) => {
		stopProfileListeners();
		applicantUnsubscribeRef.current =
			getRealTimeDocument(collections.applicants, uid, (data) => setApplicant(data as Applicant | null)) ?? null;
		memberUnsubscribeRef.current =
			getRealTimeDocument(collections.members, uid, (data) => setMember(data as Member | null)) ?? null;
	}, [stopProfileListeners]);

	const logout = useCallback(() => {
		logoutUser();
		clearUserData();
	}, [clearUserData]);

	useEffect(() => {
		mobileHandoffPendingRef.current = mobileHandoffPending;
	}, [mobileHandoffPending]);

	useEffect(() => {
		const { cleanup, handoff } = installMobileWebViewAuth(auth);
		let cancelled = false;
		void handoff.finally(() => {
			if (cancelled) return;
			mobileHandoffPendingRef.current = false;
			setMobileHandoffPending(false);
			// If custom-token sign-in failed, release the Loader (auth listener may
			// have skipped setLoading(false) while the handoff was pending).
			if (!auth.currentUser) {
				setLoading(false);
			}
		});
		return () => {
			cancelled = true;
			cleanup();
		};
	}, []);

	useEffect(() => {
		const authStateSubscription = onAuthStateChanged(auth, (currentUser) => {
			const nextUid = currentUser?.uid ?? null;
			const previousUid = userUidRef.current;

			// Same signed-in user (token/session refresh): keep the tree mounted so
			// live Daily calls are not destroyed by a full-app Loader remount.
			if (previousUid && nextUid && previousUid === nextUid) {
				setUser(currentUser);
				if (!mobileHandoffPendingRef.current) setLoading(false);
				return;
			}

			if (!currentUser) {
				clearUserData();
				// Expo secure-browser handoff: first auth event is often null before
				// signInWithCustomToken finishes. Keep Loader up so RouteGuard does
				// not bounce to /login and lose the deep link.
				if (!mobileHandoffPendingRef.current) setLoading(false);
				return;
			}

			// First auth resolution or a real user switch.
			userUidRef.current = nextUid;
			setUser(currentUser);
			setMember(null);
			setApplicant(null);
			subscribeToProfiles(currentUser.uid);
			setLoading(false);
		});

		return () => {
			if (authStateSubscription) authStateSubscription();
			clearUserData();
		};
	}, [clearUserData, subscribeToProfiles]);

	const role = useMemo<UserTypeValue | null>(() => {
		if (applicant && member) return UserType.both;
		if (applicant) return UserType.applicant;
		if (member) return UserType.member;
		return null;
	}, [applicant, member]);

	usePresenceHeartbeat(user, role, member, applicant);

	const values = useMemo<AuthContextValue>(
		() => ({
			user,
			member,
			applicant,
			role,
			loading,
			logout,
		}),
		[user, member, applicant, role, loading, logout]
	);

	if (loading || mobileHandoffPending) {
		return <Loader />;
	}

	return <AuthContext.Provider value={values}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
	const context = useContext(AuthContext);
	if (context === undefined) {
		throw new Error('useAuth must be used within an AuthProvider');
	}
	return context;
};
